import { femaleQuestionMedium, maleQuestionMedium, bulbMedium } from '@/assets/icons';
import {
  HAVING_NO_PROBLEMS,
  HAVING_NO_PROBLEMS_SOURCE,
  FORCE_STANDARDS,
  FORCE_STANDARDS_SOURCE,
  WITHOUT_DATA,
  WITHOUT_DATA_SOURCE,
} from './quotes';

const MANAGEMENT_OF_PERFORMANCE = `
<p>
  Performance management through continuous improvement must be customer-driven. The customer's need must be the top priority for the company's value creation.
  This customer can be the entity to which a product is sold, at the end of a transaction, but it can also be an in-house customer, i.e. another team or entity within the same company.<br>
  <strong>Performance</strong> is the ability to achieve total customer satisfaction in terms of the quality of the output products, and the related costs and delivery times (QCD), in the most efficient way possible.
  And it implies having the most efficient process possible to meet that customer's need.
</p>
<br>
<p>
  There are five standard components in most areas: S for Safety, Q for Quality, C for Cost, D for Delivery, and M for Management.
  The letter used does not matter as long as everyone understands the meaning of these 5 themes. Of course, some areas will require the use of a sixth
  or even a seventh theme, but be careful not to get overwhelmed by the number of indicators.<br>
  <strong>M</strong> is often the last theme mentioned, and many companies link very weak indicators that only allow for reporting, such as absenteeism or late arrivals of employees.
  And yet it is an essential key to continuous improvement.<br>
  It is through <strong>Management</strong> that good dynamism, ownership and commitment to the company's improvement strategy can be ensured.<br>
  In order to distinguish real management of performance from simple reporting, the people involved in performance, i.e. the creators of value in the field, must take ownership of the indicators, and it is these same people
  who must define the management indicators and the objectives set in order to commit to the approach. This requires breaking out of a pyramid system in which indicators are defined from above but which hinders initiative and autonomy.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${FORCE_STANDARDS}"</span>
  <br>
  <span class="author">${FORCE_STANDARDS_SOURCE}</span>
</div>
<h2 class="basic-sub-title">
  Measurement
</h2>
<p>
  Let us first clarify the meaning of Gemba: Gemba is the place where value is created. We also the word 'field' or 'floor'. In a factory, the Gemba is the workshop where products are created or processed.
  In a call centre, for instance, the Gemba is the floors dedicated to the staff answering the phone.<br>
  The data is measured on the Gemba, and that is the first key point in the logical order of the continuous improvement approach.<br>
  Data is the raw material for continuous improvement. It allows being factual and prevents judgement, approximation or guessing. And by measuring this data,
  people can be sure that the actions carried out by individuals and teams have the right impact.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${WITHOUT_DATA}"</span>
  <br>
  <span class="author">${WITHOUT_DATA_SOURCE}</span>
</div>
<p>
  Nowadays, too many decisions are made on the basis of judgement; good performance management eradicates assumptions and replaces them with tangible data.
  Measurement therefore enables gathering data and gaining an objective perspective on a process.
</p>


<ul>
  Measurement may be:
  <li>in real time: via a SPC (Statistical Process Control) system for example, which collects data in real time,</li>
  <li>periodic: every hour, the number of pieces produced is counted,</li>
  <li>by sampling or survey: to measure customer satisfaction, for example.</li>
</ul>
<p>
  Regardless of the type of measurement or its frequency, it will be connected to a precise indicator (or KPI, Key Performance Indicator).
  In order to define a good indicator, it must be related in some way to customer satisfaction.<br>
  Two types of indicators can be used: process indicators and result indicators.
  They should tell us whether the result is compliant and whether the process has been properly followed.<br>
  We generally have results indicators in companies, but process indicators are often missing, although it is often the non-compliance of a process that leads to a lack of quality or a missed delivery time, for example.
</p>
<div class="ia-sensei-example">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${bulbMedium}"></path></svg>
  <div class="example-title">As an example :</div>
  We are going to follow a recipe for a cake. The recipe will naturally give us the right process indicators that we can follow, while the result of the cake making is the cake itself.
  As process indicators, we may want to choose the oven temperature, the baking time, the order of the ingredients, ... and the result indicators could be
  the size and texture of the cake, a survey of guests, etc.
</div>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Okay, so the measurement of the data is the inputting of the indicators?</span>
</div>
<p>
  That's part of it, yes, but it's not just that. Data measurement is about looking at things factually, so indicator values, of course, but also all the things that are visible or verifiable by facts.
  When a problem is detected, it must be described, not assumed, but verified with factual data.
  When doing a field tour, people must not make approximations, nor judge, nor see, they must discuss and analyze.
  Measurement allows analysis and breaking down of problems.
</p>
<h2 class="basic-sub-title">
  Problems spotting
</h2>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${maleQuestionMedium}"></path></svg>
  <span class="question">Damn, I found another problem this morning.</span>
</div>
<p>
  Great! So you have the opportunity to solve it and improve, go ahead!<br>
  Problems are the best opportunities for improvement you can have. But first let's define exactly what a problem is:<br>
  A problem is a discrepancy between the current situation and an ideal situation.<br>
  The ideal situation can be a standard or an indicator objective, for example. This assumes, of course, that standards and objectives have already been set, hence the prerequisites of management and measurement.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Ok, I have understood how to spot my problems. But how can I detect them earlier, and how can I anticipate them?</span>
</div>
<p>
  That's an excellent question. Complex statistical analysis tools, such as control charts, for example, exist to limit the variability of the data. But this kind of tool can be tricky to set up.<br>
  But there is a much simpler and very effective tool: the <strong>field tour</strong>.
  The field tour at the management level is an essential procedure that ensures that the Gemba masters the standards and anticipates any discrepancies.
  But it is not just a managerial tool. Thanks to Shizen, the field tour is within everyone's reach and can be set up, planned and launched in just a few clicks.<br>
  A field tour can even be carried out by several people to check facts, implement safety actions, identify root causes, and implement and standardize countermeasures to problems to prevent recurrence.<br>
  Some companies, that are mature in continuous improvement, encourage employees to carry out daily field tours of their workstations.
  This way, staff are more involved in the process, gain autonomy and interact more regularly with other teams and management levels to solve their problems.
</p>
<h2 class="basic-sub-title">
  Notify and take action
</h2>
<p>
  We have a good understanding of how to spot problems and we know what we can put in place to stay ahead.
  Now we have to take the right steps based on the situation. That's the whole point of having autonomous teams on the ground.<br>
  However, some situations, some problems cannot be solved without help or support.
  It's all about getting the right person or team to take charge of the problem that someone has detected.<br>
  Shizen allows to alert these people as soon as possible to support value creation in the field.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${maleQuestionMedium}"></path></svg>
  <span class="question">And when do we actually take action?</span>
</div>
<p>
  Not necessarily right away: Some problems will prompt you to implement precautionary actions or securing actions.<br>
  If a drain pipe leaks throughout the workshop, you obviously have to find a way to prevent flooding. But this action is not part of solving the problem.<br><br>
  Before solving the problem, it is required to fully understand the problem and to be sure that one can identify the real culprit.
  This is the point of the 5 whys and Ishikawa tools, which allow everyone to identify the root causes of the problems.<br>
  Once the culprit, i.e. the root cause(s), has been found, one can determine the best actions to take to eradicate the problem.
  Once these actions are defined, they must be assigned to the right people and planned.<br>
  Finally, the actions are carried out and the solutions must be standardized to prevent the problem from recurring.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">So, how can we be sure that the problem will not occur again?</span>
</div>
<p>
  By following your daily ritual meetings, your field trips and by monitoring your indicators:<br>
  Rituals are also privileged moments to highlight and raise problems.
  These daily points allow you to assess your performance indicators, the latest problems detected and the ongoing actions
  A ritual should bring together several teams, including field staff, management and support functions.
  The maintenance and quality departments can also participate in operational rituals to promote a rhythmic and regular exchange between departments, and ensure a better understanding of the indicators.<br>
  The challenge of inter-team and inter-level interaction is to maintain a dynamic reaction to problems. Sometimes problems cannot be resolved by a field team, in which case
  it will call on a support team or management to resolve them. Without a proper interaction and a management system like Shizen, the team has no follow-up on the handling and resolution of its problem.
  The lack of information will lead to frustration and a feeling of isolation, which will cause employees to stop reporting problems.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${HAVING_NO_PROBLEMS}"</span>
  <br>
  <span class="author">${HAVING_NO_PROBLEMS_SOURCE}</span>
</div>
<p>
  <br>
  Performance management is a combination of these key concepts: the measurement and understanding of factual data, the ability to analyze and detect discrepancies
  and finally good processes to alert and act at the right level.
  Shizen provides a complete performance management system to drive and monitor performance at every hierarchical level.
  Neo assists you in every facet of the system to help or guide you on all the topics.
</p>
`;

const MANAGEMENT_OF_PERFORMANCE_TITLE = 'Management of performance';

export default {
  MANAGEMENT_OF_PERFORMANCE,
  MANAGEMENT_OF_PERFORMANCE_TITLE,
};
